// src/stores/useLayerSetsStore.js
//
// Manages named layer state snapshots (layer sets) for the active project.
//
// A snapshot captures:
//   registry   — which imagery/analysis/user layers are visible
//   assets     — wells.enabled, geometry.point, geometry.line, filters
//   static     — visibleById for operational layers
//   aoi        — selectedAoiId for LayerPanel map visibility
//   trayOrder  — ordered array of layer IDs for Active Layers tray
//
// is_default: if true, automatically applied when the project is bootstrapped.

import { create } from "zustand";
import { fetchWithAuth } from "../api/fetchWithAuth";
import { useLayerRegistryStore } from "./useLayerRegistryStore";
import { useLayersStore } from "./useLayersStore";
import { useProjectStore } from "./useProjectStore";
import { useAoiStore } from "./useAoiStore";
import { useImageryStackStore } from "./useImageryStackStore";

// ─────────────────────────────────────────────────────────────────────────────
// SNAPSHOT HELPERS
// ─────────────────────────────────────────────────────────────────────────────

export function captureSnapshot() {
  const registry = useLayerRegistryStore.getState().layers;
  const layerState = useLayersStore.getState();
  const aoiState = useAoiStore.getState();
  const trayOrder = useLayerSetsStore.getState().trayOrder;

  // Capture non-imagery registry entries by ID (existing behavior).
  // Imagery is captured separately below from the stack store.
  const registrySnap = {};
  Object.entries(registry).forEach(([id, l]) => {
    if (l.scope === "imagery") return;   // skip — handled by imagery snap
    if (l.visible) registrySnap[id] = true;
  });

  // Imagery: capture as (series_key, time_mode, pinned_date) tuples.
  // These survive sessions because they don't depend on ephemeral layer_ids.
  const imagerySnap = useImageryStackStore.getState().stack.map(entry => ({
    series_key: entry.series_key,
    time_mode: entry.time_mode,
    pinned_date: entry.pinned_date,
    opacity: entry.opacity,
    visible: entry.visible,
  }));

  const staticSnap = {};
  Object.entries(layerState.visibleById ?? {}).forEach(([id, on]) => {
    if (on) staticSnap[id] = true;
  });

  // FEATURES-tab catalog toggles (vector_assets). Captured like `static` so
  // they're governed by the set model; well_pad is excluded (it lives in
  // `assets.pads`, captured below).
  const featureTypesSnap = {};
  Object.entries(layerState.activeFeatureTypes ?? {}).forEach(([key, on]) => {
    if (on) featureTypesSnap[key] = true;
  });

  const assetsSnap = {};
  Object.entries(layerState.assets ?? {}).forEach(([assetId, assetState]) => {
    assetsSnap[assetId] = {
      enabled: assetState.enabled,
      geometry: { ...assetState.geometry },
      filters: [...(assetState.filters ?? [])],
    };
  });

  return {
    registry: registrySnap,
    imagery: imagerySnap,                  // NEW
    static: staticSnap,
    featureTypes: featureTypesSnap,        // FEATURES-tab catalog toggles
    assets: assetsSnap,
    aoi: aoiState.selectedAoiId ?? null,
    trayOrder: [...trayOrder],
  };
}

export function applySnapshot(snapshot) {
  if (!snapshot) return;

  const registryStore = useLayerRegistryStore.getState();
  const layerStore = useLayersStore.getState();
  const imageryStack = useImageryStackStore.getState();

  // Imagery: clear current stack, then recreate from snapshot.
  // Using a clean-slate approach because the stack is small and reconciling
  // diffs is more complex than just rebuilding.
  const currentImagery = imageryStack.stack;
  for (const entry of currentImagery) {
    imageryStack.removeLayer(entry.layer_id);
  }
  for (const item of snapshot.imagery ?? []) {
    imageryStack.addLayer(item.series_key, {
      time_mode: item.time_mode,
      pinned_date: item.pinned_date,
      opacity: item.opacity,
      visible: item.visible,
    });
  }

  // Non-imagery registry layers (existing behavior, scoped to skip imagery).
  const currentRegistry = registryStore.layers;
  Object.values(currentRegistry).forEach(l => {
    if (l.scope === "imagery") return;     // imagery handled above
    const shouldBeOn = !!snapshot.registry?.[l.id];
    if (l.visible !== shouldBeOn) registryStore.toggleLayer(l.id);
  });

  // Static layers
  const currentVisible = layerStore.visibleById ?? {};
  Object.keys(snapshot.static ?? {}).forEach(id => {
    if (!currentVisible[id]) layerStore.toggleLayer(id);
  });
  Object.entries(currentVisible).forEach(([id, on]) => {
    if (on && !snapshot.static?.[id]) layerStore.toggleLayer(id);
  });

  // FEATURES-tab catalog toggles — replace wholesale with the saved set (turns
  // off any feature not in the snapshot, on those that are) in a single write;
  // VectorFeatureLayers re-renders from it. Absent on pre-fix sets → {} → all
  // features off, which is the correct default. well_pad rides assets.pads.
  layerStore.setActiveFeatureTypes(snapshot.featureTypes ?? {});

  // Asset state (unchanged)
  Object.entries(snapshot.assets ?? {}).forEach(([assetId, saved]) => {
    const current = layerStore.assets?.[assetId];
    if (!current) return;

    if (current.enabled !== saved.enabled) layerStore.toggleAsset(assetId);

    Object.entries(saved.geometry ?? {}).forEach(([geom, shouldBeOn]) => {
      const currentOn = current.geometry?.[geom];
      if (currentOn !== shouldBeOn) layerStore.toggleAssetGeometry(assetId, geom);
    });

    (saved.filters ?? []).forEach(filterId => {
      if (!current.filters?.includes(filterId))
        layerStore.toggleAssetFilter(assetId, filterId);
    });
    (current.filters ?? []).forEach(filterId => {
      if (!saved.filters?.includes(filterId))
        layerStore.toggleAssetFilter(assetId, filterId);
    });
  });

  if ("aoi" in snapshot) {
    useAoiStore.getState().setSelectedAoiForMap(snapshot.aoi ?? null);
  }

  if (Array.isArray(snapshot.trayOrder)) {
    useLayerSetsStore.getState().setTrayOrder(snapshot.trayOrder);
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// STORE
// ─────────────────────────────────────────────────────────────────────────────

export const useLayerSetsStore = create((set, get) => ({

  sets: [],
  loading: false,
  error: null,
  trayOrder: [], // ordered array of layer IDs — drives Active Layers sort

  // ── Restore barrier ─────────────────────────────────────────────────────
  //
  // False while a project bootstrap is between "started" and "the default set
  // has been applied". applySnapshot is a WHOLESALE write of layer state — it
  // reconciles every asset's geometry back to the saved value — so anything
  // that sets layer state of its own during that window is silently undone.
  //
  // THAT IS NOT HYPOTHETICAL AND IT IS WHY THIS FLAG EXISTS. WellFinder turns
  // the RRC surface wells on when its layer mounts. Navigating Explore →
  // WellFinder on a cold load mounted WellLayer while the bootstrap was still
  // awaiting the default set; applySnapshot then toggled wells.geometry.point
  // back to the snapshot's value and the wells never appeared. Going back to
  // Explore and returning worked, because by then the bootstrap had finished —
  // "it works the second time" was the whole shape of the bug report.
  //
  // A BARRIER RATHER THAN A DELAY. The alternative fixes were a setTimeout or
  // a retry, both of which make the ordering a race that usually lands the
  // right way instead of an ordering that is stated. A consumer waits on the
  // fact it actually depends on: the restore is done.
  //
  // Starts TRUE so that surfaces outside a project bootstrap — global scope,
  // or a direct entry that never runs one — are not blocked waiting for an
  // event that will not arrive. beginRestore() closes it; restoreDefault
  // reopens it on every exit path, success or failure.
  layerSetsRestored: true,

  beginRestore: () => set({ layerSetsRestored: false }),

  // Released by MapPage when a bootstrap fails before it reaches restoreDefault.
  // Without this a failed project load would leave the barrier closed forever
  // and WellFinder would stop turning the RRC wells on at all — trading an
  // ordering bug for a deadlock.
  endRestore: () => set({ layerSetsRestored: true }),

  // ── Tray order ──────────────────────────────────────────────────────────

  setTrayOrder: (order) => set({ trayOrder: order }),

  reorderTray: (activeId, overId, currentIds) => {
    // currentIds = activeLayers.map(l => l.id) — the full live list
    // We use it to seed trayOrder if it's empty (first drag)
    const { trayOrder } = get();

    // Seed from live list if trayOrder is empty or stale
    const base = trayOrder.length > 0 ? trayOrder : [...currentIds];

    // Ensure both IDs are present in base
    const withActive = base.includes(activeId) ? base : [...base, activeId];
    const withBoth = withActive.includes(overId) ? withActive : [...withActive, overId];

    const oldIndex = withBoth.indexOf(activeId);
    const newIndex = withBoth.indexOf(overId);

    const next = [...withBoth];
    next.splice(oldIndex, 1);
    next.splice(newIndex, 0, activeId);

    set({ trayOrder: next });
  },

  // ── Load ────────────────────────────────────────────────────────────────

  loadSets: async (projectId) => {
    if (!projectId || projectId === "global") return;
    set({ loading: true, error: null });
    try {
      const res = await fetchWithAuth(`/api/v1/${projectId}/layer-sets`);
      if (!res.ok) throw new Error("Failed to load layer sets");
      const sets = await res.json();
      set({ sets, loading: false });
    } catch (err) {
      console.error("[useLayerSetsStore] loadSets failed", err);
      set({ loading: false, error: err.message });
    }
  },

  restoreDefault: async (projectId) => {
    if (!projectId || projectId === "global") {
      set({ layerSetsRestored: true });
      return false;
    }
    try {
      const res = await fetchWithAuth(`/api/v1/${projectId}/layer-sets/default`);
      if (res.status === 404) return false;
      if (!res.ok) throw new Error("Failed to fetch default layer set");
      const defaultSet = await res.json();
      if (typeof defaultSet.snapshot === "string") {
        defaultSet.snapshot = JSON.parse(defaultSet.snapshot);
      }
      applySnapshot(defaultSet.snapshot);
      // console.log("[useLayerSetsStore] restored default set:", defaultSet.name);
      return true;
    } catch (err) {
      console.error("[useLayerSetsStore] restoreDefault failed", err);
      return false;
    } finally {
      // EVERY EXIT PATH, INCLUDING 404 AND THROW. The barrier says "applySnapshot
      // will not run again for this bootstrap", which is true whether the
      // snapshot applied, was absent, or failed to load. Releasing it only on
      // success would leave a project with no default set waiting forever —
      // and no default set is the common case, not an error.
      set({ layerSetsRestored: true });
    }
  },

  saveSet: async (name, isDefault = false) => {
    const projectId = useProjectStore.getState().activeProjectId;
    if (!projectId || projectId === "global") return null;
    const snapshot = captureSnapshot();
    try {
      const res = await fetchWithAuth(`/api/v1/${projectId}/layer-sets`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, snapshot, is_default: isDefault }),
      });
      if (!res.ok) throw new Error(await res.text());
      const created = await res.json();
      if (typeof created.snapshot === "string") {
        created.snapshot = JSON.parse(created.snapshot);
      }
      set(s => ({
        sets: [
          ...(isDefault ? s.sets.map(x => ({ ...x, is_default: false })) : s.sets),
          created,
        ],
      }));
      return created;
    } catch (err) {
      console.error("[useLayerSetsStore] saveSet failed", err);
      return null;
    }
  },

  applySet: (setObj) => {
    const snapshot = typeof setObj.snapshot === "string"
      ? JSON.parse(setObj.snapshot)
      : setObj.snapshot;
    applySnapshot(snapshot);
  },

  updateSet: async (setId) => {
    const projectId = useProjectStore.getState().activeProjectId;
    if (!projectId) return;
    const snapshot = captureSnapshot();
    try {
      const res = await fetchWithAuth(
        `/api/v1/${projectId}/layer-sets/${setId}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ snapshot }),
        }
      );
      if (!res.ok) throw new Error(await res.text());
      const updated = await res.json();
      if (typeof updated.snapshot === "string") {
        updated.snapshot = JSON.parse(updated.snapshot);
      }
      set(s => ({ sets: s.sets.map(x => x.id === setId ? updated : x) }));
      return updated;
    } catch (err) {
      console.error("[useLayerSetsStore] updateSet failed", err);
      return null;
    }
  },

  setAsDefault: async (setId, isDefault) => {
    const projectId = useProjectStore.getState().activeProjectId;
    if (!projectId) return;
    try {
      const res = await fetchWithAuth(
        `/api/v1/${projectId}/layer-sets/${setId}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ is_default: isDefault }),
        }
      );
      if (!res.ok) throw new Error(await res.text());
      set(s => ({
        sets: s.sets.map(x => ({
          ...x,
          is_default: x.id === setId ? isDefault : (isDefault ? false : x.is_default),
        })),
      }));
    } catch (err) {
      console.error("[useLayerSetsStore] setAsDefault failed", err);
    }
  },

  renameSet: async (setId, name) => {
    const projectId = useProjectStore.getState().activeProjectId;
    if (!projectId) return;
    try {
      const res = await fetchWithAuth(
        `/api/v1/${projectId}/layer-sets/${setId}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name }),
        }
      );
      if (!res.ok) throw new Error(await res.text());
      const updated = await res.json();
      if (typeof updated.snapshot === "string") {
        updated.snapshot = JSON.parse(updated.snapshot);
      }
      set(s => ({ sets: s.sets.map(x => x.id === setId ? updated : x) }));
    } catch (err) {
      console.error("[useLayerSetsStore] renameSet failed", err);
    }
  },

  deleteSet: async (setId) => {
    const projectId = useProjectStore.getState().activeProjectId;
    if (!projectId) return;
    try {
      const res = await fetchWithAuth(
        `/api/v1/${projectId}/layer-sets/${setId}`,
        { method: "DELETE" }
      );
      if (!res.ok) throw new Error(await res.text());
      set(s => ({ sets: s.sets.filter(x => x.id !== setId) }));
    } catch (err) {
      console.error("[useLayerSetsStore] deleteSet failed", err);
    }
  },

  saveAsDefault: async () => {
    const projectId = useProjectStore.getState().activeProjectId;
    if (!projectId || projectId === "global") return null;
    const snapshot = captureSnapshot();
    const existingDefault = get().sets.find(s => s.is_default);

    if (existingDefault) {
      try {
        const res = await fetchWithAuth(
          `/api/v1/${projectId}/layer-sets/${existingDefault.id}`,
          {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ snapshot }),
          }
        );
        if (!res.ok) throw new Error(await res.text());
        const updated = await res.json();
        if (typeof updated.snapshot === "string") {
          updated.snapshot = JSON.parse(updated.snapshot);
        }
        set(s => ({ sets: s.sets.map(x => x.id === existingDefault.id ? updated : x) }));
        return updated;
      } catch (err) {
        console.error("[useLayerSetsStore] saveAsDefault (update) failed", err);
        return null;
      }
    }

    return get().saveSet("Default", true);
  },

  reset: () => set({ sets: [], loading: false, error: null, trayOrder: [] }),
}));